import { AnnuityRates } from '~/types';
import { roundToDecimal, truncateToSigFig } from './numbers';

export const calculateMaxAnnuity = (retirementAge: number, purchasePrice: number, rates: AnnuityRates) => {
    const { minRate, maxRate } = getRatesForAge(retirementAge, rates);
    const rate = interpolateRate(purchasePrice, 10000, 100000, minRate, maxRate);

    let annuity = purchasePrice * rate;
    let calculatedPurchasePrice = calculatePurchasePrice(retirementAge, annuity, rates);

    while (calculatedPurchasePrice > purchasePrice) {
        annuity -= 0.01;
        calculatedPurchasePrice = calculatePurchasePrice(retirementAge, annuity, rates);
    }

    return annuity;
};

export const calculatePurchasePrice = (retirementAge: number, targetAnnuity: number, rates: AnnuityRates) => {
    const minPrice = 10000;
    const maxPrice = 100000;
    const { minRate, maxRate } = getRatesForAge(retirementAge, rates);
    const lowerIncomeThreshold = roundToDecimal(minRate * minPrice, 2);
    const upperIncomeThreshold = roundToDecimal(maxRate * maxPrice, 2);
    const rate = interpolateRate(targetAnnuity, lowerIncomeThreshold, upperIncomeThreshold, minRate, maxRate);

    return Math.ceil(targetAnnuity / rate);
};

const interpolateRate = (value: number, minValue: number, maxValue: number, minRate: number, maxRate: number) => {
    if (value <= minValue) return minRate;
    if (value >= maxValue) return maxRate;
    return minRate + ((value - minValue) * (maxRate - minRate)) / (maxValue - minValue);
};

const getRatesForAge = (retirementAge: number, rates: AnnuityRates) => {
    return retirementAge === 66
        ? { minRate: rates?.sixtySix10k, maxRate: rates?.sixtySix100k }
        : { minRate: rates?.sixtySeven10k, maxRate: rates?.sixtySeven100k };
};

export const calculateProjectedValue = (
    age: number,
    retirementAge: number,
    pensionAmount: number,
    monthlyContributions: number
) => {
    const growthRate = 0.05;
    const inflationRate = 0.02;
    const chargeRate = 0.01;
    const escalationRate = 0.035;

    const term = Math.max(0, retirementAge - (age + 0.5));
    const rateOfReturn = (1 + growthRate) * Math.pow(1 - chargeRate / 365, 365) - 1;

    let fundBeforeInflation = pensionAmount;

    if (term !== 0) {
        for (let t = 1; t <= term * 12; t++) {
            const contribution = monthlyContributions * Math.pow(1 + escalationRate, Math.floor((t - 1) / 12));
            fundBeforeInflation = (fundBeforeInflation + contribution) * Math.pow(1 + rateOfReturn, 1 / 12);
        }
    }

    const projectedValue = fundBeforeInflation / Math.pow(1 + inflationRate, term);

    return truncateToSigFig(projectedValue, 3);
};
